import {
  List,
  Datagrid,
  TextField,
  EditButton,
  DeleteButton,
  DateField,
  Create,
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  Toolbar,
  SaveButton,
  BooleanInput,
  BooleanField,
  FunctionField,
  ReferenceInput,
  Filter,
  NumberField,
  ReferenceField,
  NumberInput,
  required,
  useRecordContext,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";
import { useState, useEffect } from "react";

// Get token from localStorage
const getToken = () => {
  try {
    const tokenData = JSON.parse(localStorage.getItem("token"));
    return tokenData?.access_token || tokenData;
  } catch (error) {
    console.error("Error getting token:", error);
    return null;
  }
};

// 🟦 UploadImage Component for Create (Auto-upload after save)
const CreateUploadImage = ({ onFileSelect }) => {
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    console.log("📁 File selected:", selectedFile?.name);
    setFile(selectedFile);
    onFileSelect(selectedFile);
  };

  return (
    <div style={{ marginTop: "8px" }}>
      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
      />
      {file && (
        <p className="text-sm text-green-600 mt-2">
          ✅ Image selected: {file.name}
        </p>
      )}
    </div>
  );
};

// 🟦 UploadImage Component for Edit (Manual upload)
const EditUploadImage = () => {
  const record = useRecordContext();
  const [file, setFile] = useState(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleUpload = async () => {
    console.log("🔼 Starting image upload...");
    console.log("📝 Record ID:", record?.id);
    console.log("📁 Selected file:", file?.name);

    if (!file) {
      console.log("⚠️ No file selected");
      notify("Please choose an image file first", { type: "warning" });
      return;
    }

    if (!record?.id) {
      console.log("❌ No record ID available");
      notify("Record ID not available", { type: "error" });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file);
      console.log("📦 FormData created");

      const uploadUrl = `${process.env.REACT_APP_URL}/admin/gallery/upload/${record.id}`;
      console.log("🌐 Upload URL:", uploadUrl);

      const token = getToken();
      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData,
      });

      console.log("📡 Upload response status:", res.status);
      console.log("📡 Upload response ok:", res.ok);

      if (!res.ok) {
        const errorText = await res.text();
        console.error("❌ Upload failed with response:", errorText);
        throw new Error(errorText || "Upload failed");
      }

      const result = await res.json();
      console.log("✅ Upload successful, response:", result);

      notify("✅ Image uploaded successfully", { type: "success" });

      // Refresh the data without reloading the page
      console.log("🔄 Refreshing data...");
      refresh();

    } catch (error) {
      console.error("❌ Upload failed:", error);
      notify(`❌ Failed to upload image: ${error.message}`, { type: "error" });
    }
  };

  return (
    <div style={{ marginTop: "8px" }}>
      <input
        type="file"
        accept="image/*"
        onChange={(e) => {
          const selectedFile = e.target.files[0];
          console.log("📁 Edit - File selected:", selectedFile?.name);
          setFile(selectedFile);
        }}
      />
      <button
        type="button"
        className="bg-blue-500 text-white px-4 py-2 rounded ml-2 hover:bg-blue-600"
        onClick={handleUpload}
        disabled={!file}
      >
        Upload Image
      </button>
      {file && (
        <p className="text-sm text-green-600 mt-2">
          Ready to upload: {file.name}
        </p>
      )}
    </div>
  );
};

// 🟩 Filter for List
const GalleryFilter = (props) => (
  <Filter {...props}>
    <SelectInput
      source="isActive"
      choices={[
        { id: true, name: "Active" },
        { id: false, name: "Inactive" },
      ]}
      alwaysOn
    />
    <ReferenceInput
      source="categoryId"
      reference="media-categories"
      filter={{ type: "gallery" }}
      alwaysOn
    >
      <SelectInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

// 🟦 Gallery List
export const GalleryList = (props) => {
  const baseUrl = process.env.REACT_APP_URL;

  return (
    <List {...props} filters={<GalleryFilter />}>
      <Datagrid rowClick="edit">
        <TextField source="id" />
        <TextField source="title" />
        <ReferenceField
          source="categoryId"
          reference="media-categories"
          label="Category"
        >
          <TextField source="name" />
        </ReferenceField>

        <FunctionField
          label="Image"
          render={(record) => {
            if (!record.image) {
              return <span style={{ color: "#999" }}>No Image</span>;
            }

            const imageUrl = record.image.startsWith("http")
              ? record.image
              : `${baseUrl}/${record.image.replace(/^\//, "")}`;

            return (
              <img
                src={imageUrl}
                alt={record.title}
                style={{
                  maxWidth: 100,
                  maxHeight: 100,
                  objectFit: "contain",
                  borderRadius: 4,
                }}
              />
            );
          }}
        />

        <NumberField source="sortOrder" label="Order" />
        <BooleanField source="isActive" label="Active" />
        <DateField source="createdAt" label="Created" showTime />
        <EditButton />
        <DeleteButton mutationMode="pessimistic" />
      </Datagrid>
    </List>
  );
};

// 🟦 Custom Create Component that completely bypasses RA's save
const CustomGalleryCreate = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    sortOrder: 0,
    isActive: true,
  });

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        console.log("📋 Fetching categories...");
        const categoriesUrl = `${process.env.REACT_APP_URL}/admin/media-categories?type=gallery&isActive=true`;
        const token = getToken();
        
        console.log("🔑 Token available:", !!token);
        console.log("🌐 Categories URL:", categoriesUrl);

        const response = await fetch(categoriesUrl, {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });

        console.log("📡 Categories response status:", response.status);
        console.log("📡 Categories response ok:", response.ok);

        if (response.ok) {
          const result = await response.json();
          console.log("✅ Categories fetched:", result.data);
          setCategories(result.data || []);
        } else {
          console.error("❌ Failed to fetch categories, status:", response.status);
          const errorText = await response.text();
          console.error("❌ Categories error response:", errorText);
        }
      } catch (error) {
        console.error("❌ Error fetching categories:", error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (file) => {
    console.log("📁 Create - File selected for auto-upload:", file?.name);
    setSelectedFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🚀 Starting gallery creation process...");
    console.log("📝 Form data:", formData);
    console.log("📁 Selected file:", selectedFile?.name);

    // Validate required fields
    if (!formData.title.trim()) {
      notify("Title is required", { type: "error" });
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Create the gallery item first
      console.log("📦 Step 1: Creating gallery item...");
      const createUrl = `${process.env.REACT_APP_URL}/admin/gallery`;
      console.log("🌐 Create URL:", createUrl);

      // Prepare payload with proper data types
      const createPayload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        sortOrder: parseInt(formData.sortOrder) || 0,
        isActive: Boolean(formData.isActive),
        // Only include categoryId if it's a valid number
        ...(formData.categoryId && { categoryId: parseInt(formData.categoryId) })
      };

      console.log("📤 Create payload:", createPayload);
      console.log("🔢 categoryId type:", typeof createPayload.categoryId);
      console.log("🔢 sortOrder type:", typeof createPayload.sortOrder);
      console.log("🔢 isActive type:", typeof createPayload.isActive);

      const token = getToken();
      const createResponse = await fetch(createUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(createPayload),
      });

      console.log("📡 Create response status:", createResponse.status);
      console.log("📡 Create response ok:", createResponse.ok);

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        console.error("❌ Create failed with error:", errorData);
        throw new Error(errorData.message || "Failed to create gallery item");
      }

      const createResult = await createResponse.json();
      console.log("✅ Create successful, response:", createResult);

      const newGalleryId = createResult.data.id;
      console.log("🆕 New gallery ID:", newGalleryId);

      // Step 2: If image was selected, upload it automatically
      let uploadSuccess = true;
      let uploadMessage = "";

      if (selectedFile) {
        console.log("📁 Step 2: Uploading image...");
        try {
          const uploadFormData = new FormData();
          uploadFormData.append("image", selectedFile);
          console.log("📦 FormData created for upload");

          const uploadUrl = `${process.env.REACT_APP_URL}/admin/gallery/upload/${newGalleryId}`;
          console.log("🌐 Upload URL:", uploadUrl);

          console.log("⏳ Sending upload request...");
          const uploadResponse = await fetch(uploadUrl, {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${token}`
            },
            body: uploadFormData,
          });

          console.log("📡 Upload response status:", uploadResponse.status);
          console.log("📡 Upload response ok:", uploadResponse.ok);

          if (!uploadResponse.ok) {
            let uploadError;
            try {
              uploadError = await uploadResponse.json();
              console.error("❌ Upload failed with JSON error:", uploadError);
            } catch (e) {
              const errorText = await uploadResponse.text();
              console.error("❌ Upload failed with text error:", errorText);
              uploadError = { message: errorText || "Upload failed" };
            }
            throw new Error(uploadError.message || "Image upload failed");
          }

          const uploadResult = await uploadResponse.json();
          console.log("✅ Upload successful, response:", uploadResult);

          uploadMessage = "Gallery item created and image uploaded successfully";

        } catch (uploadError) {
          console.error("❌ Image upload failed:", uploadError);
          uploadSuccess = false;
          uploadMessage = `Gallery created but image upload failed: ${uploadError.message}`;
        }
      } else {
        console.log("ℹ️ No image selected for upload");
        uploadMessage = "Gallery item created successfully (no image uploaded)";
      }

      // Step 3: Show appropriate notification
      if (uploadSuccess) {
        notify(uploadMessage, { type: "success" });
      } else {
        notify(uploadMessage, { type: "warning" });
      }

      // Step 4: Refresh and redirect ONLY after everything is done
      console.log("🔄 Refreshing data and redirecting...");
      refresh();

      // Redirect to list after a short delay to ensure refresh completes
      setTimeout(() => {
        console.log("➡️ Redirecting to list...");
        redirect("list", "gallery");
      }, 1000);

    } catch (error) {
      console.error("❌ Create process failed:", error);
      notify(`❌ Failed to create gallery item: ${error.message}`, { type: "error" });
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ padding: '1em' }}>
      <div style={{ marginBottom: '2em' }}>
        <h1>Create Gallery Item</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1em' }}>
          <label htmlFor="title">Title *</label>
          <input
            id="title"
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            required
            style={{ width: '100%', padding: '0.5em', border: '1px solid #ccc', borderRadius: '4px', background: 'white' }}
            placeholder="Enter gallery item title"
          />
        </div>

        <div style={{ marginBottom: '1em' }}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            style={{ width: '100%', padding: '0.5em', minHeight: '100px', border: '1px solid #ccc', borderRadius: '4px', background: 'white' }}
            placeholder="Enter description (optional)"
          />
        </div>

        <div style={{ marginBottom: '1em' }}>
          <label htmlFor="categoryId">Category</label>
          <select
            id="categoryId"
            value={formData.categoryId}
            onChange={(e) => handleInputChange('categoryId', e.target.value)}
            style={{ width: '100%', padding: '0.5em', border: '1px solid #ccc', borderRadius: '4px', background: 'white' }}
            disabled={isLoadingCategories}
          >
            <option value="">Select a category (optional)</option>
            {isLoadingCategories ? (
              <option value="" disabled>Loading categories...</option>
            ) : categories.length > 0 ? (
              categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))
            ) : (
              <>
                {/* Fallback categories if API fails */}
                <option value="0">General</option>
               
              </>
            )}
          </select>
          {isLoadingCategories && (
            <p className="text-xs text-gray-500 mt-1">Loading categories from API...</p>
          )}
          {!isLoadingCategories && categories.length === 0 && (
            <p className="text-xs text-yellow-600 mt-1">
              Using fallback categories. Check API connection.
            </p>
          )}
        </div>

        <div style={{ marginBottom: '1em' }}>
          <label htmlFor="sortOrder">Sort Order</label>
          <input
            id="sortOrder"
            type="number"
            value={formData.sortOrder}
            onChange={(e) => handleInputChange('sortOrder', e.target.value)}
            style={{ width: '100%', padding: '0.5em', border: '1px solid #ccc', borderRadius: '4px', background: 'white' }}
            min="0"
          />
        </div>

        <div style={{ marginBottom: '1em' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
            />
            Active
          </label>
        </div>

        {/* Image upload - will auto-upload after creation */}
        <div className="border w-full p-3 border-primary rounded-lg" style={{ marginBottom: '1em' }}>
          <p className="text-sm text-gray-600 mb-2">
            Upload Gallery Image (Optional - will auto-upload after creation)
          </p>
          <CreateUploadImage onFileSelect={handleFileSelect} />
          {selectedFile && (
            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
              <p className="text-sm text-green-700">
                <strong>Image ready for upload:</strong> {selectedFile.name}
              </p>
              <p className="text-xs text-green-600 mt-1">
                This image will be automatically uploaded after the gallery item is created.
              </p>
            </div>
          )}
        </div>

        <div style={{ marginTop: '2em' }}>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              padding: '0.7em 1.5em',
              border: 'none',
              borderRadius: '4px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.6 : 1,
              fontSize: '1em',
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create & Upload'}
          </button>
        </div>

        {isSubmitting && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-blue-700">
              ⏳ Creating gallery item and uploading image...
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Please wait, you will be redirected to the list when complete.
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

// 🟦 Gallery Create - Using custom component
export const GalleryCreate = (props) => {
  return <CustomGalleryCreate {...props} />;
};

// 🟦 Gallery Edit
const GalleryEditToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

export const GalleryEdit = (props) => (
  <Edit title="Edit Gallery Item" {...props}>
    <SimpleForm toolbar={<GalleryEditToolbar />}>
      <TextInput source="title" fullWidth validate={[required()]} />
      <TextInput source="description" multiline fullWidth label="Description" />
      <ReferenceInput
        source="categoryId"
        reference="media-categories"
        label="Category"
        filter={{ type: "gallery", isActive: true }}
      >
        <SelectInput optionText="name" allowEmpty />
      </ReferenceInput>
      <NumberInput source="sortOrder" label="Sort Order" />
      <BooleanInput source="isActive" label="Active" />

      {/* Show current image */}
      <FunctionField
        label="Current Image"
        render={(record) => {
          console.log("🖼️ Current record in edit:", record);

          if (!record.image) {
            return <span style={{ color: "#999" }}>No image uploaded</span>;
          }

          const baseUrl = process.env.REACT_APP_URL;
          const imageUrl = record.image.startsWith("http")
            ? record.image
            : `${baseUrl}/${record.image.replace(/^\//, "")}`;

          return (
            <div>
              <p className="text-sm text-gray-600 mb-2">Current Image:</p>
              <img
                src={imageUrl}
                alt={record.title}
                style={{
                  maxWidth: 200,
                  maxHeight: 200,
                  objectFit: "contain",
                  borderRadius: 4,
                  border: "1px solid #e5e7eb",
                }}
              />
              <p className="text-xs text-gray-500 mt-1">Image path: {record.image}</p>
            </div>
          );
        }}
      />

      {/* Upload new image */}
      <div className="border w-full p-3 border-primary rounded-lg mt-4">
        <p className="text-sm text-gray-600 mb-2">
          Upload New Image
        </p>
        <EditUploadImage />
      </div>
    </SimpleForm>
  </Edit>
);